import React, { useEffect, useRef, useState } from "react";
import {
  CheckCircle2,
  KeyRound,
  MessageSquareText,
  RefreshCw,
  Send,
  ShieldCheck,
  Smartphone,
  XCircle,
} from "lucide-react";

const getConfigId = (item) =>
  item?.pk_message_config_id ||
  item?.messageConfigId ||
  item?.message_config_id ||
  item?.id ||
  "";

const getPartnerName = (item) =>
  item?.otp_partner || item?.otpPartner || item?.partner || "Unknown";

const getStatus = (item) =>
  String(item?.status || "N")
    .trim()
    .toUpperCase() === "Y"
    ? "Y"
    : "N";

const formatDate = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString();
};

export default function MessageConfigurationManager({
  getMessageConfig,
  updateMessageConfig,
  loginGateway,
  messageConfigLoading = false,
  loginGatewayLoading = false,
  refreshKey = 0,
}) {
  const [configType, setConfigType] = useState("0");
  const [configurations, setConfigurations] = useState([]);
  const [updatingId, setUpdatingId] = useState("");

  const [gatewayForm, setGatewayForm] = useState({
    number: "",
    otp: "",
  });

  const [gatewayResponse, setGatewayResponse] = useState(null);
  const [gatewayError, setGatewayError] = useState("");

  /*
   * useChaafo ke functions har parent render par recreate ho sakte hain.
   * Ref use karne se configType effect unnecessary loop me nahi jayega.
   */
  const getMessageConfigRef = useRef(getMessageConfig);

  useEffect(() => {
    getMessageConfigRef.current = getMessageConfig;
  }, [getMessageConfig]);

  const loadConfigurations = async (typeValue = configType) => {
    const result = await getMessageConfigRef.current?.(typeValue);

    const list = Array.isArray(result?.response) ? result.response : [];

    setConfigurations(list);

    return result;
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const result = await getMessageConfigRef.current?.(configType);

      if (cancelled) return;

      setConfigurations(Array.isArray(result?.response) ? result.response : []);
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [configType, refreshKey]);

  const handleToggleStatus = async (item) => {
    const id = String(getConfigId(item) || "").trim();

    if (!id) return;

    const currentStatus = getStatus(item);
    const nextStatus = currentStatus === "Y" ? "N" : "Y";

    setUpdatingId(id);

    try {
      const result = await updateMessageConfig({
        messageConfigId: id,
        status: nextStatus,
      });

      if (!result) return;

      /*
       * Local update first so UI instantly reflect kare.
       */
      setConfigurations((prev) =>
        prev.map((config) =>
          String(getConfigId(config)) === id
            ? {
                ...config,
                status: nextStatus,
              }
            : config,
        ),
      );

      /*
       * Server se final source refresh.
       */
      await loadConfigurations(configType);
    } finally {
      setUpdatingId("");
    }
  };

  const handleGatewayFormChange = (field, value) => {
    setGatewayForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    setGatewayError("");
  };

  /*
   * step = 1 -> Send OTP
   */
  const handleSendOtp = async () => {
    const number = gatewayForm.number.trim();

    if (!number) {
      setGatewayError("Please enter mobile number");
      return;
    }

    setGatewayError("");
    setGatewayResponse(null);

    const result = await loginGateway({
      number,
      otp: "",
      step: "1",
    });

    setGatewayResponse(result);
  };

  /*
   * step = 2 -> Verify OTP
   */
  const handleVerifyOtp = async () => {
    const number = gatewayForm.number.trim();
    const otp = gatewayForm.otp.trim();

    if (!number) {
      setGatewayError("Please enter mobile number");
      return;
    }

    if (!otp) {
      setGatewayError("Please enter OTP");
      return;
    }

    setGatewayError("");
    setGatewayResponse(null);

    const result = await loginGateway({
      number,
      otp,
      step: "2",
    });

    setGatewayResponse(result);
  };

  return (
    <div className="space-y-6">
      {/* MESSAGE CONFIG */}
      <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                <MessageSquareText size={20} />
              </div>

              <div>
                <h2 className="text-lg font-bold text-slate-900">
                  Message Configuration
                </h2>

                <p className="text-sm text-slate-500">
                  Configure the OTP messaging partner used by Chaafo.
                </p>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => loadConfigurations(configType)}
            disabled={messageConfigLoading}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw
              size={16}
              className={messageConfigLoading ? "animate-spin" : ""}
            />
            Refresh
          </button>
        </div>

        {/* TYPE FILTER */}
        <div className="mt-5 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setConfigType("0")}
            className={`rounded-xl px-4 py-2 text-sm font-bold transition ${
              configType === "0"
                ? "bg-emerald-600 text-white shadow-sm"
                : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            All Configs
            <span className="ml-1.5 text-xs opacity-70">type=0</span>
          </button>

          <button
            type="button"
            onClick={() => setConfigType("1")}
            className={`rounded-xl px-4 py-2 text-sm font-bold transition ${
              configType === "1"
                ? "bg-emerald-600 text-white shadow-sm"
                : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            Type 1<span className="ml-1.5 text-xs opacity-70">type=1</span>
          </button>
        </div>

        {/* CONFIG LIST */}
        <div className="mt-5">
          {messageConfigLoading && configurations.length === 0 ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {[1, 2].map((item) => (
                <div
                  key={item}
                  className="h-36 animate-pulse rounded-2xl bg-slate-100"
                />
              ))}
            </div>
          ) : configurations.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {configurations.map((item) => {
                const id = String(getConfigId(item));
                const partner = getPartnerName(item);
                const status = getStatus(item);
                const active = status === "Y";
                const isUpdating = updatingId === id;

                return (
                  <div
                    key={id}
                    className={`rounded-2xl border p-4 transition ${
                      active
                        ? "border-emerald-200 bg-emerald-50/40"
                        : "border-slate-200 bg-white"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex min-w-0 items-center gap-3">
                        <div
                          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                            active
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          <Smartphone size={20} />
                        </div>

                        <div className="min-w-0">
                          <h3 className="truncate text-base font-bold text-slate-900">
                            {partner}
                          </h3>

                          <p className="mt-0.5 text-xs text-slate-500">
                            Config ID: {id}
                          </p>
                        </div>
                      </div>

                      <div
                        className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${
                          active
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-red-100 text-red-600"
                        }`}
                      >
                        {active ? (
                          <CheckCircle2 size={13} />
                        ) : (
                          <XCircle size={13} />
                        )}

                        {active ? "Active" : "Inactive"}
                      </div>
                    </div>

                    {item?.in_date && (
                      <div className="mt-4 border-t border-slate-200/80 pt-3">
                        <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                          Last Updated
                        </p>

                        <p className="mt-1 text-xs font-medium text-slate-600">
                          {formatDate(item.in_date)}
                        </p>
                      </div>
                    )}

                    <div className="mt-4 flex items-center justify-between border-t border-slate-200/80 pt-4">
                      <div>
                        <p className="text-sm font-semibold text-slate-700">
                          Enable Partner
                        </p>

                        <p className="text-[11px] text-slate-400">
                          Status: {status}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleToggleStatus(item)}
                        disabled={messageConfigLoading || isUpdating}
                        className={`relative h-7 w-12 rounded-full transition ${
                          active ? "bg-emerald-500" : "bg-slate-300"
                        } disabled:cursor-not-allowed disabled:opacity-60`}
                        aria-label={`${
                          active ? "Disable" : "Enable"
                        } ${partner}`}
                      >
                        <span
                          className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-sm transition-all ${
                            active ? "left-6" : "left-1"
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 py-12 text-center">
              <MessageSquareText size={28} className="mx-auto text-slate-400" />

              <h3 className="mt-3 font-bold text-slate-800">
                No configuration found
              </h3>

              <p className="mt-1 text-sm text-slate-500">
                No message configuration was returned for type=
                {configType}.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* LOGIN GATEWAY TEST */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
            <ShieldCheck size={17} />
          </div>

          <div>
            <h2 className="text-base font-bold text-slate-900">
              Login Gateway
            </h2>

            <p className="text-xs text-slate-500">Test OTP gateway.</p>
          </div>
        </div>

        <div className="mt-4 max-w-md space-y-3">
          {/* STEP 1 */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Send size={14} className="text-emerald-600" />

                <h3 className="text-sm font-bold text-slate-900">
                  Mobile Number
                </h3>
              </div>

              <span className="rounded-md bg-white px-1.5 py-0.5 text-[9px] font-bold text-slate-500 ring-1 ring-slate-200">
                Step 1
              </span>
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={gatewayForm.number}
                onChange={(e) =>
                  handleGatewayFormChange("number", e.target.value)
                }
                placeholder="Enter mobile number"
                className="h-9 min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-xs text-slate-800 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
              />

              <button
                type="button"
                onClick={handleSendOtp}
                disabled={loginGatewayLoading || !gatewayForm.number.trim()}
                className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-3 text-xs font-bold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loginGatewayLoading ? (
                  <RefreshCw size={13} className="animate-spin" />
                ) : (
                  <Send size={13} />
                )}
                Send OTP
              </button>
            </div>
          </div>

          {/* STEP 2 */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <KeyRound size={14} className="text-blue-600" />

                <h3 className="text-sm font-bold text-slate-900">Verify OTP</h3>
              </div>

              <span className="rounded-md bg-white px-1.5 py-0.5 text-[9px] font-bold text-slate-500 ring-1 ring-slate-200">
                Step 2
              </span>
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={gatewayForm.otp}
                onChange={(e) => handleGatewayFormChange("otp", e.target.value)}
                placeholder="Enter OTP"
                className="h-9 min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-xs text-slate-800 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
              />

              <button
                type="button"
                onClick={handleVerifyOtp}
                disabled={
                  loginGatewayLoading ||
                  !gatewayForm.number.trim() ||
                  !gatewayForm.otp.trim()
                }
                className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3 text-xs font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loginGatewayLoading ? (
                  <RefreshCw size={13} className="animate-spin" />
                ) : (
                  <ShieldCheck size={13} />
                )}
                Verify
              </button>
            </div>
          </div>

          {gatewayError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
              {gatewayError}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
